import React, { useRef, useState } from 'react';
import PasswordInput from 'components/input/PasswordInput';
import TextIconInput from 'components/input/TextIconInput';
import { useFormik } from 'formik';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Alert, Form, Row, Button, Spinner } from 'react-bootstrap';
import { routers } from 'router/router';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getStudent, login, setStudentId, googleLogin } from 'app/authorization/authorizationSlice';
import {
  getStripeSubscriptionInfo,
  getCurrentBasicDetail,
  getContactDetail,
} from 'app/basicInformationSlice/basicInformationSlice';
import Cookies from 'js-cookie';
import { getHighSchoolData } from 'app/highSchoolSlice/highSchoolSlice';
import { checkEnableParseJSON, userpilotDomain } from 'components/helper/helper';
import { get } from 'lodash';
import { Userpilot } from 'userpilot';
import { STRIPE_SUBSCRIPTION_STATUS, STRIPE_PRICE_NAME } from 'constants/common';
import { GoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import { redirectProgram } from 'screen/common/RedirectHelper';
import { USER_ROLE } from 'constants/common';
import SessionExistsModal from './SessionExistsModal';
import { setIsConfirmedByPhone } from 'utils/sessionStorage';
import partnerServices from 'services/partnerServices';
import counselorServices from 'services/counselorServices';
import {
  CAM_UPGRADE_REQUIRED_EVENT,
  completeCamInboundOAuth,
  hasCamOAuthHandoff,
  hasPendingCamOAuthRedirect,
  isCamUpgradeModalPending,
  notifyCamUpgradeRequired,
  prepareCamOAuthLoginPage,
  persistPendingCamOAuthParams,
  resolveCamOAuthAfterStudentLogin,
  trySeedSessionFromCamOAuthHandoff,
} from 'utils/camAuth';
import { getAccessToken } from 'utils/sessionStorage';
import {
  clearPartnerInviteContext,
  getEffectiveSearch,
  getInviteTokenFromSearch,
  getPersistedInviteToken,
  mergeSearchPreserveInvite,
  persistInviteMeta,
  persistInviteToken,
  setInviteRedirectAfterOnboarding,
  stripInviteTokenFromSearch,
  syncInviteTokenDocumentSearchToHashRouter,
} from 'utils/partnerInvite';
// import { authenticateSettings } from 'components/helper/appConfig';

// Component nút Google tùy chỉnh
const CustomGoogleButton = ({ onGoogleLoginSuccess, onGoogleLoginError }: any) => {
  const googleLoginRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);

  // Xử lý khi người dùng nhấp vào nút tùy chỉnh
  const handleCustomButtonClick = () => {
    // Tìm và kích hoạt nút Google ẩn
    if (googleLoginRef.current) {
      const googleButton = googleLoginRef.current.querySelector('div[role="button"]');
      if (googleButton instanceof HTMLElement) {
        googleButton.click();
      } else {
        toast.error('Cannot connect to Google. Login failed');
      }
    }
  };

  return (
    <>
      {/* Nút tùy chỉnh */}
      <Button
        className="w-100 my-3 custom-google-btn"
        variant="light"
        onClick={handleCustomButtonClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          height: 42,
          borderRadius: '8px',
          border: '1px solid #dadce0',
          boxShadow: isHovered ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 12px',
          backgroundColor: isHovered ? '#f8f9fa' : '#ffffff',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <img
            src="https://developers.google.com/identity/images/g-logo.png"
            alt="Google logo"
            style={{ width: 18, height: 18, marginRight: 10 }}
          />
          <span
            style={{
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'Roboto, Arial, sans-serif',
              color: '#3c4043',
            }}
          >
            Continue with Google
          </span>
        </div>
      </Button>

      {/* Component Google Login ẩn */}
      <div ref={googleLoginRef} style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
        <GoogleLogin
          onSuccess={onGoogleLoginSuccess}
          onError={() => {
            if (onGoogleLoginError) {
              onGoogleLoginError();
            } else {
              toast.error('Google login failed');
            }
          }}
          type="standard"
          theme="outline"
          text="signin_with"
          shape="rectangular"
          logo_alignment="center"
        />
      </div>
    </>
  );
};

interface Props {}

const FormLogin = (props: Props) => {
  const dispatch = useAppDispatch();
  const authorizationStore = useAppSelector((store) => store.authorization);

  const history = useHistory();
  const location = useLocation();

  const [camOAuthBridging, setCamOAuthBridging] = React.useState(false);

  const showCamUpgradeGate = React.useCallback(() => {
    setCamOAuthBridging(false);
    notifyCamUpgradeRequired();
  }, []);

  const closeSessionExistsModal = React.useCallback(() => {
    setSessionExistsModal({
      open: false,
      message: '',
      activeSessions: [],
    });
  }, []);

  React.useEffect(() => {
    const onCamUpgradeRequired = () => {
      closeSessionExistsModal();
      setCamOAuthBridging(false);
    };
    window.addEventListener(CAM_UPGRADE_REQUIRED_EVENT, onCamUpgradeRequired);
    return () => window.removeEventListener(CAM_UPGRADE_REQUIRED_EVENT, onCamUpgradeRequired);
  }, [closeSessionExistsModal]);

  /**
   * PATH 1 (CAM → login): clear stale GM session; OAuth runs only after form submit.
   * PATH 2 (handoff): seed session and auto-complete without password.
   */
  React.useLayoutEffect(() => {
    syncInviteTokenDocumentSearchToHashRouter();
    if (isCamUpgradeModalPending()) {
      return;
    }
    prepareCamOAuthLoginPage();

    if (!hasPendingCamOAuthRedirect() || !hasCamOAuthHandoff()) {
      return;
    }

    trySeedSessionFromCamOAuthHandoff();

    if (!getAccessToken()) {
      return;
    }

    setCamOAuthBridging(true);
    void completeCamInboundOAuth({
      dispatch,
      getStripeSubscriptionInfo,
      onUpgradeRequired: showCamUpgradeGate,
      onRedirecting: () => setCamOAuthBridging(true),
    })
      .then((completed) => {
        if (!completed) {
          setCamOAuthBridging(false);
        }
      })
      .finally(() => {
        if (!hasPendingCamOAuthRedirect()) {
          setCamOAuthBridging(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolvedSearch = React.useMemo(
    () => getEffectiveSearch(location.search),
    [location.search]
  );

  const [inviteBanner, setInviteBanner] = React.useState<{
    loading: boolean;
    valid: boolean | null;
    inviteeEmail?: string;
    inviteKind?: string;
    error?: string | null;
  }>({ loading: false, valid: null });

  React.useEffect(() => {
    const t = getInviteTokenFromSearch(resolvedSearch) || getPersistedInviteToken();
    if (!t) {
      setInviteBanner({ loading: false, valid: null });
      return;
    }
    persistInviteToken(t);
    let cancelled = false;
    setInviteBanner((s) => ({ ...s, loading: true }));
    partnerServices
      .validateInviteToken(t)
      .then((res: any) => {
        if (cancelled) return;
        if (res?.valid) {
          persistInviteMeta(res);
          setInviteBanner({
            loading: false,
            valid: true,
            inviteeEmail: res.invitee_email,
            inviteKind: res.invite_kind,
          });
        } else {
          clearPartnerInviteContext();
          setInviteBanner({
            loading: false,
            valid: false,
            error: res?.message || 'This invitation is invalid or expired.',
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setInviteBanner({
          loading: false,
          valid: false,
          error: 'Could not verify the invitation.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedSearch]);

  const tryAcceptPartnerInvite = async (): Promise<boolean> => {
    const inviteToken =
      getPersistedInviteToken() || getInviteTokenFromSearch(resolvedSearch);
    if (!inviteToken) return false;
    try {
      await partnerServices.acceptPartnerInvite(inviteToken);
      clearPartnerInviteContext();
      toast.success('Invitation accepted.');
      const path = history.location.pathname || '/public/login';
      const cleanSearch = stripInviteTokenFromSearch(history.location.search || '');
      history.replace({ pathname: path, search: cleanSearch });
      setInviteBanner({ loading: false, valid: null });
      return true;
    } catch {
      return false;
    }
  };
  const [sessionExistsModal, setSessionExistsModal] = useState({
    open: false,
    message: '',
    activeSessions: [] as Array<{ device: string; lastSeen: string; ipAddress: string }>,
  });
  const [loginCredentials, setLoginCredentials] = useState<{ email: string; password: string } | null>(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email').required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Store credentials for potential retry with force=true
        setLoginCredentials(values);
        persistPendingCamOAuthParams();

        const res: any = await dispatch(login(values));

        if (res?.error?.message === 'User not found') {
          toast.error('User not found');
          history.push({
            pathname: routers.register.path,
            search: mergeSearchPreserveInvite(resolvedSearch, {}),
          });
          setSubmitting(false);
          return;
        }
        if (res?.error?.message === 'Network Error') {
          toast.error('Network Error: Please check your internet connection or backend server.');
          setSubmitting(false);
          return;
        }

        if (res.payload) {
          //#region catch error from api PassportStrategy
          // Check for SESSION_EXISTS error (statusCode 430)
          if (res.payload.statusCode === 430 && res.payload.code === 'SESSION_EXISTS') {
            setSessionExistsModal({
              open: true,
              message: `Another active GradMap session is still open. Click Continue to log you out of any other active session to avoid any potential data loss.`,
              activeSessions: res.payload.activeSessions || [],
            });
            setSubmitting(false);
            return;
          }
          
          if (res.payload.statusCode === 400) {
            if (res.payload.message === 'User not found') {
              toast.error('User not found');
              history.push({
                pathname: routers.register.path,
                search: mergeSearchPreserveInvite(resolvedSearch, {}),
              });
              return;
            }

            // else show error message
            if (res.payload.errorCode === 100) {
              toast.error('Invalid User or Password');
            }
            else
              toast.error(res.payload.message);

            return;
          } else
          if (res.payload.statusCode === 500) {
            toast.error(res.payload.message);
            return;
          }         
          //#endregion

          // Process successful login
          await processLoginSuccess(res);
        }
      } catch (error:any) {
        if (error.response) {
          // Server Error
          console.error('Server Error:', error.response.status, error.response.data);
        } else if (error.request) {
          // Network Error
          console.error('Network Error:', error.request);
        } else {
          // Other error
          console.error('Error:', error.message);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  async function getHighSchoolName(studentId: any) {
    try {
      let hightSchoolData = await dispatch(getHighSchoolData(studentId)).unwrap();
      let currentHighSchool = checkEnableParseJSON(hightSchoolData.high_school_array, []);
      if (currentHighSchool) {
        return currentHighSchool[0]?.high_school_name;
      }
    } catch (ex) {
      console.log('Error:', ex);
    }

    return '';
  }

  const processLoginSuccess = async (res: any) => {
    // PATH 1: CAM → GradMap login → subscription check → back to CAM
    persistPendingCamOAuthParams();
    if (hasPendingCamOAuthRedirect()) {
      sessionStorage.setItem('userRole', res.payload?.user_role ?? '');
      sessionStorage.setItem('currentUserEmail', res.payload?.email ?? '');
      if (res.payload?.student_id) {
        sessionStorage.setItem('session_student_id', String(res.payload.student_id));
      }
      setCamOAuthBridging(true);
      try {
        const oauthResult = await resolveCamOAuthAfterStudentLogin({
          dispatch,
          getStripeSubscriptionInfo,
          login: {
            access_token: res.payload?.access_token,
            student_id: res.payload?.student_id,
            user_role: res.payload?.user_role,
          },
        });
        if (oauthResult === 'skipped') {
          return;
        }
      } finally {
        setCamOAuthBridging(false);
      }
      return;
    }

    const inviteAccepted = await tryAcceptPartnerInvite();
    const user_role = res.payload?.user_role ?? null;
    // const is_confirmed_by_phone = true; //res.payload?.is_confirmed_by_phone ?? false;
    const is_confirmed_by_phone = res.payload?.is_confirmed_by_phone ?? false;
    setIsConfirmedByPhone(is_confirmed_by_phone);
    if (is_confirmed_by_phone) {
      const info = await dispatch(getCurrentBasicDetail(res.payload.student_id));
      let highschoolName = await getHighSchoolName(res.payload.student_id);
      let userPlan = '';
      sessionStorage.setItem('currentUserEmail', res.payload.email);
      sessionStorage.setItem('userRole', user_role);
      sessionStorage.setItem('session_student_id', res.payload.student_id);
      const subStripeInfo: any = await dispatch(getStripeSubscriptionInfo(res.payload?.student_id));
      let stripeStatus: any = subStripeInfo ? get(subStripeInfo, 'payload.subcription_status', '') : null;

      if (info.payload?.use_extension && subStripeInfo.payload?.subcription_plan?.includes('gold')) {
        userPlan = 'gold';
      } else if (info.payload?.use_extension && subStripeInfo.payload?.subcription_plan?.includes('silver_plus')) {
        userPlan = 'silver-plus';
      } else if (subStripeInfo.payload?.subcription_plan?.includes('gradmap_starter')) {
        userPlan = 'gradmap-starter';
      } else if (!info.payload?.use_extension && !subStripeInfo.payload?.subcription_plan?.includes('gradmap_starter')) {
        userPlan = 'silver';
      }

      if (window.Intercom && info.payload) {
        window.Intercom('boot', {
          name: info.payload.last_name,
          email: info.payload.email,
          plan: userPlan,
          user_hash: info.payload.user_hash,
          gm_user_id: res.payload.student_id,
          student_first_name: info.payload.first_name,
          student_last_name: info.payload.last_name,
          student_email: info.payload.email,
          current_high_school: highschoolName,
          home_address_state: info.payload.state,
          user_type: info.payload.user_role,
          environment: process.env.REACT_APP_ENV,
          graduation_year: info.payload.year_finish_high_school,
          stripe_status: stripeStatus ?? '',
        });
      }

      if (user_role === USER_ROLE.counselor || user_role === USER_ROLE.parent) {
        if (inviteAccepted && user_role === USER_ROLE.parent) {
          history.push(`${routers.settings.path}?active-tab=6`);
        } else {
          history.push({
            pathname: routers.dashBoard.path,
            search: `?role=${user_role}`,
          });
        }
      } else {
        if (subStripeInfo?.payload?.subcription_plan === '') {
          Cookies.set('request_id', res.payload.student_id);
          Cookies.set('userEmail', res.payload.email);
          history.push(routers.payment.path);
        } else {
          const student = await dispatch(getStudent(res.payload.student_id));
          const contactDetail: any = await dispatch(getContactDetail(res.payload.student_id));
          const additionalInfo = checkEnableParseJSON(student.payload.additional_info, {});

          if (stripeStatus === 'trialing') {
            stripeStatus = 'Trial Mode';
          }

          if (student.payload?.trial_count > 0 &&
              stripeStatus === STRIPE_SUBSCRIPTION_STATUS.canceled
          ) {
            stripeStatus = 'Expired Trial';
          }

          if (window.Intercom && info.payload) {
            window.Intercom('update', {
              phone: contactDetail.payload?.phone_number,
              major_1: get(additionalInfo, 'majors_interest_1', ''),
              major_2: get(additionalInfo, 'majors_interest_2', ''),
              parent_first_name: get(additionalInfo, 'parent_first_name', ''),
              parent_last_name: get(additionalInfo, 'parent_last_name', ''),
              parent_relationship: get(additionalInfo, 'relationship_to_you', ''),
              parent_email: get(additionalInfo, 'prefered_email', ''),
              accept_contact: get(additionalInfo, 'accept_contact', ''),
              stripe_status: stripeStatus ?? '',
            });
          }
          if (inviteAccepted && res.payload.user_role === USER_ROLE.student) {
            history.push(`${routers.settings.path}?active-tab=6`);
          } else {
            history.push({
              pathname: routers.dashBoard.path,
              search: `?role=${res.payload.user_role}`,
            });
          }
        }
      }

      Userpilot.identify((res.payload.student_id ?? 'undefined') + userpilotDomain(res.payload.user_role), {
        plan: userPlan ?? 'undefined',
        stripeStatus: stripeStatus ?? 'undefined',
        userType: info.payload.user_role ?? 'undefined',
        name: info.payload.first_name ?? 'undefined',
        created_at: info.payload.created_at ?? 'undefined',
        firstName: info.payload.first_name ?? 'undefined',
        lastName: info.payload.last_name ?? 'undefined',
        email: info.payload.email ?? 'undefined',
        graduationYear: info.payload.year_finish_high_school ?? 'undefined',
        school: highschoolName ?? 'undefined',
      });
    } 
    else {
      await dispatch(setStudentId(res.payload.student_id));
      if (inviteAccepted) {
        setInviteRedirectAfterOnboarding();
      }
      history.push({
        pathname: routers.register.path,
        search: mergeSearchPreserveInvite(stripInviteTokenFromSearch(resolvedSearch), {
          step: 'phone',
        }),
      });
    }

    if (res.payload?.student_id) {
      await dispatch(getStudent(res.payload?.student_id));

      let redirectId = Cookies.get('redirectId') ?? '';
      let catId = Cookies.get('catId') ?? '';
      await redirectProgram(redirectId, catId);
    }
  };

  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      // Extract the credential (ID token) from the response
      const tokenId = credentialResponse.credential;

      const res: any = await dispatch(
        googleLogin({
          tokenId: tokenId,
          force: true
        })
      );

      // const is_confirmed_by_phone = true; //res.payload?.is_confirmed_by_phone ?? false;
      const is_confirmed_by_phone = res.payload?.is_confirmed_by_phone ?? false;
      setIsConfirmedByPhone(is_confirmed_by_phone);
      if (res.payload) {
        console.log('res:', res);
        if (res.payload?.should_register) {
          history.push({
            pathname: routers.register.path,
            search: mergeSearchPreserveInvite(resolvedSearch, {
              email: res.payload.email,
              first_name: res.payload?.name?.first_name || '',
              last_name: res.payload?.name?.last_name || '',
              google: 'true',
            }),
          });
          return;
        }

        //#region catch error from api PassportStrategy
        // Check for SESSION_EXISTS error (statusCode 430)
        if (res.payload.statusCode === 430 && res.payload.code === 'SESSION_EXISTS') {
          setSessionExistsModal({
            open: true,
            message: `Another active GradMap session is still open. Click Continue to log you out of any other active session to avoid any potential data loss.`,
            activeSessions: res.payload.activeSessions || [],
          });
          // setSubmitting(false);
          return;
        }
        
        if (res.payload.statusCode === 400) {
          if (res.payload.message === 'User not found') {
            toast.error('User not found');
            history.push({
              pathname: routers.register.path,
              search: mergeSearchPreserveInvite(resolvedSearch, {}),
            });
            return;
          }

          // else show error message
          if (res.payload.errorCode === 100) {
            toast.error('Invalid User or Password');
          }
          else
            toast.error(res.payload.message);

          return;
        } else
        if (res.payload.statusCode === 500) {
          toast.error(res.payload.message);
          return;
        }         
        //#endregion

        if (res.payload && is_confirmed_by_phone) {
          const inviteAccepted = await tryAcceptPartnerInvite();
          const info = await dispatch(getCurrentBasicDetail(res.payload.student_id));
          let highschoolName = await getHighSchoolName(res.payload.student_id);
          let userPlan = '';
          sessionStorage.setItem('currentUserEmail', res.payload.email);
          sessionStorage.setItem('userRole', res.payload.user_role);
          sessionStorage.setItem('session_student_id', res.payload.student_id);
          const subStripeInfo: any = await dispatch(
            getStripeSubscriptionInfo(res.payload?.student_id)
          );
          let stripeStatus = get(subStripeInfo, 'payload.subcription_status', '');

          if (info.payload?.use_extension && subStripeInfo.payload?.subcription_plan?.includes('gold')) {
            userPlan = 'gold';
          } else if (info.payload?.use_extension && subStripeInfo.payload?.subcription_plan?.includes('silver_plus')) {
            userPlan = 'silver-plus';
          } else if (subStripeInfo.payload?.subcription_plan?.includes('gradmap_starter')) {
            userPlan = 'gradmap-starter';
          } else if (!info.payload?.use_extension && !subStripeInfo.payload?.subcription_plan?.includes('gradmap_starter')) {
            userPlan = 'silver';
          }

          if (window.Intercom && info.payload) {
            window.Intercom('boot', {
              name: info.payload.last_name,
              email: info.payload.email,
              plan: userPlan,
              user_hash: info.payload.user_hash,
              gm_user_id: res.payload.student_id,
              student_first_name: info.payload.first_name,
              student_last_name: info.payload.last_name,
              student_email: info.payload.email,
              current_high_school: highschoolName,
              home_address_state: info.payload.state,
              user_type: info.payload.user_role,
              environment: process.env.REACT_APP_ENV,
              graduation_year: info.payload.year_finish_high_school,
              stripe_status: stripeStatus ?? '',
            });
          }

          if (res.payload.user_role === 'counselor') {
            try {
              const partners = await counselorServices.getPartner(res.payload.student_id);
              const firstStudentId = partners?.[0]?.student_id;
              if (firstStudentId) {
                history.push(routers.viewStudent.path.replace(':id', String(firstStudentId)));
              } else {
                history.push(routers.dashBoard.path);
              }
            } catch {
              history.push(routers.dashBoard.path);
            }
          } else {
            if (subStripeInfo.payload?.subcription_plan === '') {
              Cookies.set('request_id', res.payload.student_id);
              Cookies.set('userEmail', res.payload.email);
              history.push(routers.payment.path);
            }
            else {
              const student = await dispatch(getStudent(res.payload.student_id));
              const contactDetail: any = await dispatch(getContactDetail(res.payload.student_id));
              const additionalInfo = checkEnableParseJSON(student.payload.additional_info, {});

              if (stripeStatus === 'trialing') {
                stripeStatus = 'Trial Mode';
              }

              if (
                student.payload?.trial_count > 0 &&
                stripeStatus === STRIPE_SUBSCRIPTION_STATUS.canceled
              ) {
                stripeStatus = 'Expired Trial';
              }

              if (window.Intercom && info.payload) {
                window.Intercom('update', {
                  phone: contactDetail.payload?.phone_number,
                  major_1: get(additionalInfo, 'majors_interest_1', ''),
                  major_2: get(additionalInfo, 'majors_interest_2', ''),
                  parent_first_name: get(additionalInfo, 'parent_first_name', ''),
                  parent_last_name: get(additionalInfo, 'parent_last_name', ''),
                  parent_relationship: get(additionalInfo, 'relationship_to_you', ''),
                  parent_email: get(additionalInfo, 'prefered_email', ''),
                  accept_contact: get(additionalInfo, 'accept_contact', ''),
                  stripe_status: stripeStatus ?? '',
                });
              }

              if (
                inviteAccepted &&
                (res.payload.user_role === USER_ROLE.parent ||
                  res.payload.user_role === USER_ROLE.student)
              ) {
                history.push(`${routers.settings.path}?active-tab=6`);
              } else {
                history.push({
                  pathname: routers.dashBoard.path,
                  search: `?role=${res.payload.user_role}`,
                });
              }
            }
          }

          Userpilot.identify((res.payload.student_id ?? 'undefined') + userpilotDomain(res.payload.user_role), {
            plan: userPlan ?? 'undefined',
            stripeStatus: stripeStatus ?? 'undefined',
            userType: info.payload.user_role ?? 'undefined',
            name: info.payload.first_name ?? 'undefined',
            created_at: info.payload.created_at ?? 'undefined',
            firstName: info.payload.first_name ?? 'undefined',
            lastName: info.payload.last_name ?? 'undefined',
            email: info.payload.email ?? 'undefined',
            graduationYear: info.payload.year_finish_high_school ?? 'undefined',
            school: highschoolName ?? 'undefined',
          });
        } 
        else 
        if (!is_confirmed_by_phone) {
          const inviteAcceptedPhone = await tryAcceptPartnerInvite();
          dispatch(setStudentId(res.payload.student_id));
          if (inviteAcceptedPhone) {
            setInviteRedirectAfterOnboarding();
          }
          history.push({
            pathname: routers.register.path,
            search: mergeSearchPreserveInvite(stripInviteTokenFromSearch(resolvedSearch), {
              step: 'phone',
            }),
          });
        }
        if (res.payload?.student_id) {
          dispatch(getStudent(res.payload?.student_id));

          let redirectId = Cookies.get('redirectId') ?? '';
          let catId = Cookies.get('redirectId') ?? '';
          await redirectProgram(redirectId, catId);
        }
      }
    } catch (error) {
      console.log({ error });
    }
  };


  const handleGoogleLoginError = () => {
    toast.error('Google login failed');
  };

  const handleCloseSessionModal = () => {
    closeSessionExistsModal();
    setLoginCredentials(null);
  };

  const handleContinueLogin = async () => {
    const creds = loginCredentials;
    if (!creds) {
      handleCloseSessionModal();
      return;
    }

    closeSessionExistsModal();
    persistPendingCamOAuthParams();

    try {
      const res: any = await dispatch(login({ ...creds, force: true }));

      if (res?.error?.message === 'User not found') {
        toast.error('User not found');
        history.push({
          pathname: routers.register.path,
          search: mergeSearchPreserveInvite(resolvedSearch, {}),
        });
        return;
      }
      if (res?.error?.message === 'Network Error') {
        toast.error('Network Error: Please check your internet connection or backend server.');
        return;
      }

      if (!res?.payload) {
        return;
      }

      if (res.payload.statusCode === 430 && res.payload.code === 'SESSION_EXISTS') {
        setSessionExistsModal({
          open: true,
          message: `Another active GradMap session is still open. Click Continue to log you out of any other active session to avoid any potential data loss.`,
          activeSessions: res.payload.activeSessions || [],
        });
        setLoginCredentials(creds);
        return;
      }

      if (res.meta?.requestStatus === 'rejected') {
        toast.error(res.payload?.message || 'Login failed');
        setLoginCredentials(creds);
        return;
      }

      if (res.payload.statusCode === 400) {
        if (res.payload.message === 'User not found') {
          toast.error('User not found');
          history.push({
            pathname: routers.register.path,
            search: mergeSearchPreserveInvite(resolvedSearch, {}),
          });
          return;
        }
        if (res.payload.errorCode === 100) {
          toast.error('Invalid User or Password');
        } else {
          toast.error(res.payload.message);
        }
        return;
      }
      if (res.payload.statusCode === 500) {
        toast.error(res.payload.message);
        return;
      }

      setLoginCredentials(null);
      await processLoginSuccess(res);
    } catch (error: any) {
      if (error.response) {
        console.error('Server Error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Network Error:', error.request);
      } else {
        console.error('Error:', error.message);
      }
    }
  };

  const inviteTokenPresent =
    Boolean(getInviteTokenFromSearch(resolvedSearch)) || Boolean(getPersistedInviteToken());

  return (
    <Form onSubmit={formik.handleSubmit}>
      {inviteBanner.loading && inviteTokenPresent && (
        <Alert variant="light" className="mb-3 border">
          Checking your invitation…
        </Alert>
      )}
      {inviteBanner.valid === false && inviteBanner.error && (
        <Alert variant="warning" className="mb-3">
          {inviteBanner.error}
        </Alert>
      )}
      {inviteBanner.valid === true && inviteBanner.inviteKind && (
        <Alert variant="info" className="mb-3">
          {inviteBanner.inviteKind === 'studentInviteParent' && (
            <>You were invited to join as a <strong>parent</strong>. Sign in with the invited email.</>
          )}
          {inviteBanner.inviteKind === 'parentInviteStudent' && (
            <>You were invited to join as a <strong>student</strong>. Sign in with the invited email.</>
          )}
          {inviteBanner.inviteeEmail && (
            <span className="d-block mt-2 small text-muted">{inviteBanner.inviteeEmail}</span>
          )}
        </Alert>
      )}
      <Row className="mb-3 p-0 mx-0">
        <TextIconInput
          icon={<MailOutlineIcon />}
          mdCol={'12'}
          label={'Email'}
          name={'email'}
          values={formik.values.email}
          onChange={formik.handleChange}
          feedback={
            formik.touched.email &&
            formik.errors.email && <span className="text-danger">{formik.errors.email}</span>
          }
        />

        <PasswordInput
          mdCol={'12'}
          label={'Password'}
          name={'password'}
          values={formik.values.password}
          onChange={formik.handleChange}
          feedback={
            formik.touched.password &&
            formik.errors.password && <span className="text-danger">{formik.errors.password}</span>
          }
        />
      </Row>
      <Button
        className="m-0 p-0"
        variant="link"
        onClick={() => history.push(routers.forgotPassword.path)}
      >
        Forgot your password?
      </Button>

      {authorizationStore.status !== 'loading' ? (
        <Button
          className="w-100 my-3"
          type="submit"
          variant="primary"
          style={{
            height: 42,
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
          }}
        >
          <span className="h5 fw-bold">Login</span>
        </Button>
      ) : (
        <Button
          className="w-100 my-3"
          variant="primary"
          style={{
            height: 42,
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Spinner style={{ fontSize: 16 }} size="sm" animation="border" variant="light" />
        </Button>
      )}

      <div className="w-100 d-flex align-items-center my-3">
        <div className="border mb-1" style={{ width: 'calc(50% - 20px)' }} />
        <div className="text-field-light text-center" style={{ width: '40px' }}>
          OR
        </div>
        <div className="border mb-1" style={{ width: 'calc(50% - 20px)' }} />
      </div>

      {/* Sử dụng nút Google tùy chỉnh thay vì nút GoogleLogin mặc định */}
      <CustomGoogleButton
        onGoogleLoginSuccess={handleGoogleLogin}
        onGoogleLoginError={handleGoogleLoginError}
      />

      {/* Thêm CSS toàn cục để đảm bảo nút hoạt động đúng trong mọi môi trường */}
      <style>{`
        .custom-google-btn {
          width: 100% !important;
        }
        
        /* Ngăn các style của Google ghi đè */
        .custom-google-btn > div {
          width: 100% !important;
        }
      `}</style>

      {/* Session Exists Modal */}
      <SessionExistsModal
        open={sessionExistsModal.open && !isCamUpgradeModalPending()}
        handleClose={handleCloseSessionModal}
        handleContinue={handleContinueLogin}
        message={sessionExistsModal.message}
        activeSessions={sessionExistsModal.activeSessions}
      />

      {camOAuthBridging && !isCamUpgradeModalPending() && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255, 255, 255, 0.92)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Spinner animation="border" role="status" />
            <div style={{ marginTop: 16, fontWeight: 500 }}>Redirecting to CAM…</div>
          </div>
        </div>
      )}

    </Form>
  );
};

export default FormLogin;